import { ChevronRight, X } from 'lucide-react'
import { clsx } from 'clsx'
import { useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import {
  isNavigationPathActive,
  navigationSections,
  type NavigationItem,
} from './navigation'
import { GroupedNavigation } from './GroupedNavigation'

interface SidebarProps {
  collapsed: boolean
  mobileOpen: boolean
  onCollapsedChange: (collapsed: boolean) => void
  onMobileClose: () => void
}

function isItemActive(item: NavigationItem, pathname: string) {
  if (!item.to) return false
  if (item.to === '/dashboard') return pathname === item.to
  return pathname.startsWith(item.to)
}

function hasActiveGroupItem(item: NavigationItem, pathname: string) {
  return item.groups?.some((group) =>
    group.items.some((child) => isNavigationPathActive(pathname, child.to)),
  )
}

export function Sidebar({
  collapsed,
  mobileOpen,
  onCollapsedChange,
  onMobileClose,
}: SidebarProps) {
  const { pathname } = useLocation()
  const [hovered, setHovered] = useState(false)
  const [keyboardFocus, setKeyboardFocus] = useState(false)
  const desktopCompact = collapsed && !hovered && !keyboardFocus
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    () => ({
      proxy:
        pathname.startsWith('/resources/') &&
        !pathname.startsWith('/resources/microservice'),
    }),
  )

  const navigation = (compact: boolean, desktop = false) => (
    <nav
      className="sidebar-scroll relative min-h-0 flex-1 overflow-x-hidden overflow-y-auto px-2 py-4"
      onPointerEnter={(event) => {
        if (desktop && event.pointerType === 'mouse') setHovered(true)
      }}
      aria-label="Primary navigation"
    >
      {navigationSections.map((section, sectionIndex) => (
        <section
          key={section.label || 'main'}
          className={clsx(sectionIndex > 0 && 'mt-3')}
        >
          {section.label && (
            <div className="relative mb-1 h-5">
              <h2
                className={clsx(
                  'absolute inset-0 overflow-hidden px-3 text-[10px] font-bold tracking-[0.22em] whitespace-nowrap text-muted uppercase transition-opacity duration-200 motion-reduce:transition-none',
                  compact ? 'opacity-0' : 'opacity-100',
                )}
                aria-hidden={compact}
              >
                {section.label}
              </h2>
              <div
                aria-hidden="true"
                className={clsx(
                  'absolute top-2 left-1/2 h-px w-8 -translate-x-1/2 bg-inset transition-opacity duration-200 motion-reduce:transition-none',
                  compact ? 'opacity-100' : 'opacity-0',
                )}
              />
            </div>
          )}
          <div className="space-y-0.5">
            {section.items.map((item) => {
              const Icon = item.icon
              const childActive = hasActiveGroupItem(item, pathname)
              const active =
                isItemActive(item, pathname) || Boolean(childActive)
              const className = clsx(
                'group relative flex min-h-10 w-full items-center gap-3 overflow-hidden px-3 text-sm font-medium transition-colors',
                compact ? 'rounded-full' : 'rounded-xl',
                active
                  ? clsx(
                      'bg-gradient-to-r from-accent/20 via-accent/10 to-accent/5 text-accent',
                      !compact && 'border-l-2 border-l-[var(--accent-primary)]/80',
                    )
                  : item.migrated
                    ? 'text-secondary hover:bg-inset hover:text-primary'
                    : 'cursor-not-allowed text-muted',
              )
              const content = (
                <>
                  <Icon
                    size={18}
                    aria-hidden="true"
                    className={clsx(
                      'shrink-0',
                      active
                        ? 'text-accent'
                        : item.migrated
                          ? 'text-secondary group-hover:text-primary'
                          : 'text-muted',
                    )}
                  />
                  <span
                    className={clsx(
                      'min-w-0 flex-1 truncate text-left transition-opacity duration-200 motion-reduce:transition-none',
                      compact ? 'opacity-0' : 'opacity-100',
                    )}
                  >
                    {item.label}
                  </span>
                  {!compact && item.groups && (
                    <ChevronRight
                      size={14}
                      aria-hidden="true"
                      className={clsx(
                        'text-muted transition',
                        expandedGroups[item.id] && 'rotate-90',
                      )}
                    />
                  )}
                  {!compact && !item.migrated && !item.groups && (
                    <span className="rounded bg-inset px-1.5 py-0.5 text-[9px] text-muted">
                      pending
                    </span>
                  )}
                </>
              )

              if (item.groups) {
                const expanded = Boolean(expandedGroups[item.id])
                return (
                  <div key={item.id}>
                    <button
                      type="button"
                      title={compact ? item.label : undefined}
                      aria-expanded={compact ? undefined : expanded}
                      className={className}
                      onClick={() => {
                        if (compact) onCollapsedChange(false)
                        setExpandedGroups((current) => ({
                          ...current,
                          [item.id]: !expanded,
                        }))
                      }}
                    >
                      {content}
                    </button>
                    <div
                      aria-hidden={compact || !expanded}
                      inert={compact || !expanded}
                      className={clsx(
                        'grid transition-[grid-template-rows,opacity] duration-250 ease-out motion-reduce:transition-none',
                        !compact && expanded
                          ? 'grid-rows-[1fr] opacity-100'
                          : 'grid-rows-[0fr] opacity-0',
                      )}
                    >
                      <div className="min-h-0 overflow-hidden">
                        <GroupedNavigation
                          groups={item.groups}
                          pathname={pathname}
                          onNavigate={onMobileClose}
                        />
                      </div>
                    </div>
                  </div>
                )
              }

              if (!item.migrated || !item.to) {
                return (
                  <button
                    key={item.id}
                    type="button"
                    disabled
                    title={`${item.label} has not been migrated yet`}
                    className={className}
                  >
                    {content}
                  </button>
                )
              }

              return (
                <NavLink
                  key={item.id}
                  to={item.to}
                  title={compact ? item.label : undefined}
                  className={className}
                  onClick={onMobileClose}
                >
                  {content}
                </NavLink>
              )
            })}
          </div>
        </section>
      ))}
    </nav>
  )

  return (
    <>
      <aside
        aria-label="Desktop sidebar"
        onPointerLeave={() => {
          setHovered(false)
        }}
        onPointerDownCapture={() => setKeyboardFocus(false)}
        onFocusCapture={(event) => {
          if (event.target.matches(':focus-visible')) setKeyboardFocus(true)
        }}
        onBlurCapture={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget))
            setKeyboardFocus(false)
        }}
        className={clsx(
          'fixed top-16 bottom-0 left-0 z-40 hidden flex-col border-r border-border-default bg-[linear-gradient(180deg,color-mix(in_srgb,_var(--bg-base)_97%,_transparent)_0%,color-mix(in_srgb,_var(--bg-base)_99%,_transparent)_100%)] backdrop-blur-2xl transition-[width] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none lg:flex',
          desktopCompact ? 'w-16' : 'w-56',
        )}
      >
        {navigation(desktopCompact, true)}
        <button
          type="button"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="group absolute top-8 -right-3.5 z-50 grid size-7 place-items-center rounded-full border border-border-default bg-canvas shadow-lg transition hover:border-accent-border/50 hover:bg-accent-faint"
          onClick={() => onCollapsedChange(!collapsed)}
        >
          <ChevronRight
            size={14}
            aria-hidden="true"
            className={clsx(
              'text-muted transition group-hover:text-accent',
              !collapsed && 'rotate-180',
            )}
          />
        </button>
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-60 lg:hidden">
          <button
            type="button"
            aria-label="Close navigation"
            className="absolute inset-0 size-full bg-overlay/60 backdrop-blur-sm"
            onClick={onMobileClose}
          />
          <aside className="absolute inset-y-0 left-0 flex w-72 flex-col border-r border-accent-border bg-[linear-gradient(180deg,color-mix(in_srgb,_var(--bg-base)_99%,_transparent)_0%,var(--bg-base)_100%)]">
            <div className="flex h-16 items-center justify-between border-b border-border-default px-4">
              <div className="flex items-center gap-2">
                <img
                  src="/assets/justlogo.png"
                  alt="ForgeSphere"
                  className="h-9 w-auto"
                />
                <span className="font-heading gradient-text font-extrabold">
                  ForgeSphere
                </span>
              </div>
              <button
                type="button"
                aria-label="Close navigation"
                className="grid size-9 place-items-center rounded-lg text-muted hover:bg-inset hover:text-primary"
                onClick={onMobileClose}
              >
                <X size={18} aria-hidden="true" />
              </button>
            </div>
            {navigation(false)}
          </aside>
        </div>
      )}
    </>
  )
}
